/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  CSSProperties,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import {
  Grid,
  GridCell,
  Input,
  PageSection,
  Spacer,
  Stack,
  Icon,
  IconButton,
  TextSubtitle,
  TextBody,
  RadioGroup,
  Radio,
  SelectOption,
  Select,
  TextCaption,
  TextTitle,
  Snackbar,
  Button,
  Tooltip,
  Slider,
  PortalDestination,
  Toggle,
  Tag,
  sendToast,
} from '@pypestream/design-system';
import { useIsMountedRef, useProStudioRoutes } from '../../hooks';
import { useNavigate } from 'react-router';
import { capitalize } from 'lodash';
import { createSaveSettlement } from '../../utils';
import {
  proStudioService,
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { sendUserMessage } from '../../xstate/app.xstate-utils';
import { integratorIdOf } from '../../pages/validate/call-transcript';
import { contactCenterAccountOf } from '../../utils/voice-inbox-connection';
import { toSolutionEnvironment } from '../../utils/map-connection-to-row';
import {
  setUpVoiceInbox,
  type VoiceInboxSetupResult,
} from './set-up-voice-inbox';
import { waitForProStudioIdle } from '../../xstate/wait-for-pro-studio-idle';
import {
  UnsavedChangesValidationError,
  useUnsavedChanges,
  useVoiceSelectorModal,
} from '../modals';
import {
  TwilioConfigInput,
  VoiceAiProvider,
} from '@pypestream/api-services/urql';
import {
  AddHandoffRuleButton,
  HandoffTypeSelect,
  SkillTagSelect,
} from './handoff-rules';
import {
  buildVoiceAiConfig,
  CONTACT_CENTER_PLATFORM,
} from './build-voice-ai-config';
import type { LanguageEntry } from './languages-config';
import { LanguagesBlock, useVoiceAiLanguages } from './languages-block';
import { validateLanguages } from './validate-languages';
import { RetryMessageInput } from './retry-message-input';
import {
  clampEotThreshold,
  clampFluxUserSilenceTimeout,
  EOT_THRESHOLD_DEFAULT,
  EOT_THRESHOLD_MAX,
  EOT_THRESHOLD_MIN,
  EOT_THRESHOLD_STEP,
  FLUX_SPEECH_MODEL,
  FLUX_USER_SILENCE_TIMEOUT_MIN,
  isFluxModel,
  type MaxSpeakingTimeState,
  resolveConversationHandling,
  resolveFluxMaxSpeakingTimeOnLoad,
  resolveFluxMaxSpeakingTimeOnModelSwitch,
} from './flux-rules';

/** Off-screen but readable by assistive tech — used to name the Call Recording toggle. */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

type VoiceModelOption = {
  value: string;
  label: string;
  description: string;
  recommended?: boolean;
};

const ELEVENLABS_AUDIO_MODELS: VoiceModelOption[] = [
  {
    value: 'flash_v2_5',
    label: 'flash_v2_5',
    description:
      'Fastest generation with the lowest latency — ideal for real-time conversational AI.',
    recommended: true,
  },
  {
    value: 'flash_v2',
    label: 'flash_v2',
    description:
      'Low-latency TTS optimized for speed — good for live interactions where response time matters.',
  },
  {
    value: 'turbo_v2_5',
    label: 'turbo_v2_5',
    description:
      'Balanced speed and voice quality — best for production deployments needing both performance and naturalness.',
  },
  {
    value: 'turbo_v2',
    label: 'turbo_v2',
    description:
      'High-quality synthesis with moderate latency — suited for near-real-time use cases requiring richer voice output.',
  },
];

const GOOGLE_SPEECH_MODELS: VoiceModelOption[] = [
  {
    value: 'long',
    label: 'long',
    description:
      'Optimized for audio longer than ~1 minute — best for extended conversations and calls.',
    recommended: true,
  },
  {
    value: 'short',
    label: 'short',
    description:
      'Optimized for short utterances under ~1 minute — ideal for quick single-turn voice commands.',
  },
];

const DEEPGRAM_SPEECH_MODELS: VoiceModelOption[] = [
  {
    value: FLUX_SPEECH_MODEL,
    label: FLUX_SPEECH_MODEL,
    description:
      'Lowest-latency model. Fuses transcription and turn detection for the fastest, most natural turn-taking with fewer interruptions.',
    recommended: true,
  },
  {
    value: 'nova-3-general',
    label: 'nova-3-general',
    description:
      'Most accurate and capable STT model — handles diverse accents and noisy environments.',
  },
  {
    value: 'nova-3-medical',
    label: 'nova-3-medical',
    description:
      'Nova-3 accuracy tuned for clinical and healthcare terminology.',
  },
  {
    value: 'nova-2-general',
    label: 'nova-2-general',
    description:
      'Highly accurate general-purpose transcription for most voice AI use cases.',
  },
  {
    value: 'nova-2-atc',
    label: 'nova-2-atc',
    description:
      'Tuned for air traffic control communications and aviation-specific vocabulary.',
  },
  {
    value: 'nova-2-automotive',
    label: 'nova-2-automotive',
    description:
      'Optimized for in-car environments with road noise and automotive commands.',
  },
  {
    value: 'nova-2-conversationalai',
    label: 'nova-2-conversationalai',
    description:
      'Fine-tuned for back-and-forth dialogue in virtual assistants and chatbots.',
  },
  {
    value: 'nova-2-drivethru',
    label: 'nova-2-drivethru',
    description:
      'Trained on drive-through ordering scenarios with background noise handling.',
  },
  {
    value: 'nova-2-ea',
    label: 'nova-2-ea',
    description:
      'Optimized for earnings calls and financial analyst conversations.',
  },
  {
    value: 'nova-2-finance',
    label: 'nova-2-finance',
    description:
      'Tuned for financial terminology, tickers, and market-related vocabulary.',
  },
  {
    value: 'nova-2-medical',
    label: 'nova-2-medical',
    description:
      'Designed for clinical speech with medical terminology and dictation accuracy.',
  },
  {
    value: 'nova-2-meeting',
    label: 'nova-2-meeting',
    description:
      'Optimized for multi-speaker meeting transcription and conference calls.',
  },
  {
    value: 'nova-2-phonecall',
    label: 'nova-2-phonecall',
    description:
      'Trained on telephone audio quality — handles compressed narrow-band speech.',
  },
  {
    value: 'nova-2-video',
    label: 'nova-2-video',
    description:
      'Tuned for video content transcription with clear produced audio.',
  },
  {
    value: 'nova-2-voicemail',
    label: 'nova-2-voicemail',
    description:
      'Handles low-quality voicemail audio and short recorded messages.',
  },
];

const DEFAULT_GOOGLE_SPEECH_MODEL = GOOGLE_SPEECH_MODELS[0].value;
const DEFAULT_DEEPGRAM_SPEECH_MODEL = FLUX_SPEECH_MODEL;

// Shown in place of the editable value while Flux forces these settings to zero.
const FLUX_LOCKED_DISPLAY_VALUE = '0 (Immediately)';

// Form types for voice channel settings
export type VoiceChannelFormValues = {
  botName: string;
  env: 'sandbox' | 'live';

  /**
   * The Languages block (PSTUDIO-2950): one entry per language, each with its
   * own voice. The flat `language` / `elevenlabs*` fields of `twilio_params`
   * are derived from the starting language at save — see buildVoiceAiConfig.
   */
  languages: LanguageEntry[];
  /** Key (`en`, not `en-US`) of the language every call starts in. */
  startingLanguage: string;

  speechModel: string;
  transcriptionProvider: string;

  // phoneNumberName: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  webhookUrl: string;

  // Live Agent Platform (encoded as metadataIntegrationInfoB64)
  laPlatform: string;
  laClientId: string;
  laClientSecret: string;
  laGrantType: string;
  laInstanceUrl: string;
  laOAuthUrl: string;
  /** Contact Center voice inbox: the CC account and the inbox calls ring in. */
  laAccountId: string;
  laVoiceInboxId: string;

  // Handoff Rules (encoded as voiceHandoffOptionsB64)
  handoffRules: Array<{
    skillTag: string;
    destination: string;
    type: 'phone_number' | 'sip';
  }>;

  // Conversation Handling
  inboundMessageBuffer: number | null;
  userSilenceTimeout: number | null;
  eotThreshold: number;
  promptMaxRetries?: number | null;
  retryPromptPrefix?: string | null;
  /**
   * Call Recording (PSTUDIO-4090). Per-channel by design — CS asked for control
   * on each Voice AI channel, and the flag has nowhere else to live: it is a key
   * of this channel's `twilio_params` and Engagement has no notion of a project.
   *
   * It must stay in this payload even when untouched. The payload rebuilds the
   * whole `twilio_params` map and the request transform nulls every key the
   * client omits, so dropping it would turn recording off on the next save of
   * any unrelated field — the disconnect BK has been reporting.
   */
  enableCallRecording: boolean;
};

interface ChannelSettingsFormProps {
  type: 'create' | 'update';
  initialValues: Partial<VoiceChannelFormValues>;
  channelId?: string;
}

/** One toast for what the voice inbox setup did, and what is left to the user. */
const voiceInboxSetupMessage = ({
  connection,
  nodes,
}: VoiceInboxSetupResult): { type: 'success' | 'warning'; text: string } => {
  const problems = [
    connection === 'no-template' &&
      'Add a Contact Center connection in Live Agent Platforms first, then save again.',
    connection === 'no-pype' &&
      'The channel could not be found to connect it — save it again.',
    nodes === 'no-entry' &&
      'This solution has no standard escalation node (7900) to send calls to the inbox.',
    nodes === 'not-loaded' &&
      'Open the solution, then save this channel again to add the escalation steps.',
  ].filter(Boolean);
  if (problems.length) return { type: 'warning', text: problems.join(' ') };
  return {
    type: 'success',
    text:
      nodes === 'added' || nodes === 'rewired'
        ? 'Voice inbox connected. Escalation steps were updated in the draft — publish to make calls ring in Contact Center.'
        : 'Voice inbox connected.',
  };
};

export const VoiceChannelSettingsForm: FC<ChannelSettingsFormProps> = ({
  type,
  initialValues,
  channelId,
}) => {
  const isUpdating = type === 'update';
  const routes = useProStudioRoutes();
  const { projectId, pypeConnections } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
    pypeConnections: ctx.pypeConnections ?? [],
  }));
  const navigate = useNavigate();

  const form = useForm<VoiceChannelFormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    control,
    clearErrors,
    formState: { errors, isDirty, dirtyFields },
  } = form;

  const {
    fields: handoffFields,
    append: appendHandoffRule,
    remove: removeHandoffRule,
  } = useFieldArray({
    control,
    name: 'handoffRules',
  });

  // UI-only flag controlling the "Max Caller Speaking Time" toggle + input
  // visibility. Kept separate from the numeric value so clearing the input
  // (transient empty) doesn't collapse the field or flip the toggle off.
  const [isMaxSpeakingEnabled, setIsMaxSpeakingEnabled] = useState(
    () => initialValues.userSilenceTimeout != null
  );

  // Drives the "raised to the minimum" note under the field on Flux.
  const [raisedToMinimum, setRaisedToMinimum] = useState(false);

  // What the Flux floor overwrote, so leaving Flux can put it back. Null
  // whenever the seed did not fire — there is then nothing to restore.
  const preFluxSilenceRef = useRef<MaxSpeakingTimeState | null>(null);

  // Reset form when initialValues change (e.g., when switching from create to edit mode)
  useEffect(() => {
    reset(initialValues);
    setIsMaxSpeakingEnabled(initialValues.userSilenceTimeout != null);

    preFluxSilenceRef.current = null;

    // A channel saved under the old Flux rule carries 0, which is the dead-air
    // bug itself (PSTUDIO-3941). Show it already corrected, and say so. The
    // rule is unit-tested in flux-rules; this only applies the result.
    const maxSpeaking = resolveFluxMaxSpeakingTimeOnLoad(
      initialValues.userSilenceTimeout,
      initialValues.speechModel
    );

    setIsMaxSpeakingEnabled(maxSpeaking.enabled);
    setRaisedToMinimum(maxSpeaking.raised);

    // Dirty on purpose: the Save button is gated on isDirty (see the Snackbar
    // below), so a correction applied silently would be one the user is shown
    // but can never persist — the stored 0, and the dead air, would survive.
    if (maxSpeaking.changed) {
      setValue('userSilenceTimeout', maxSpeaking.value, { shouldDirty: true });
    }
  }, [initialValues, reset, setValue]);

  // Watch form values for real-time updates
  const watchedValues = watch();

  // Flux handles turn detection itself, so it locks Inbound Message Buffer to
  // zero and reveals End of Turn Threshold. Max Caller Speaking Time stays
  // editable — it is what arms a retry (PSTUDIO-3941).
  const isFlux = isFluxModel(watchedValues.speechModel);

  const maxSpeakingHelpText = raisedToMinimum
    ? `↑ Raised to ${FLUX_USER_SILENCE_TIMEOUT_MIN}s, the minimum.`
    : `Silent callers rely on this to trigger a retry. Minimum ${FLUX_USER_SILENCE_TIMEOUT_MIN}s.`;

  // Hoisted so the field's own onBlur can chain RHF's handler rather than
  // shadow it (the DS Input maps onBlur to the `blur` event, so last-wins).
  const maxSpeakingTimeField = register('userSilenceTimeout', {
    setValueAs: (v) =>
      v === '' || v === null || v === undefined ? null : Number(v),
    // No minimum rule on Flux: the form validates on change, so one would put
    // the field in an error state on the way to a legal number — typing "1" of
    // "12" would read as rejected. Flux RAISES instead of rejecting (design
    // frames 1000003108 → 1000003109): the floor is applied on blur, and
    // resolveConversationHandling applies it again on the way to the API.
    min: isFlux ? undefined : { value: 2, message: 'Must be 2 or greater' },
    validate: (v) =>
      v == null || Number.isInteger(v) || 'Must be a whole number',
  });

  /**
   * Keeps Max Caller Speaking Time consistent with the selected speech model.
   *
   * Switching TO Flux seeds the floor when what is there cannot serve a retry —
   * a channel "newly switched to Flux" defaults to 10s (PSTUDIO-3941 AC 2).
   *
   * Switching AWAY undoes exactly that seed. Without it, a non-Flux channel with
   * the setting switched off silently comes back on at 10s, because picking the
   * Deepgram *provider* selects Flux by default (DEFAULT_DEEPGRAM_SPEECH_MODEL)
   * — so a user can round-trip through Flux without ever choosing it by name.
   */
  const applyFluxTurnTakingRules = (newModel: string) => {
    const resolved = resolveFluxMaxSpeakingTimeOnModelSwitch({
      newModel,
      current: {
        value: form.getValues('userSilenceTimeout') ?? null,
        enabled: isMaxSpeakingEnabled,
      },
      captured: preFluxSilenceRef.current,
    });

    preFluxSilenceRef.current = resolved.captured;
    setIsMaxSpeakingEnabled(resolved.enabled);
    setRaisedToMinimum(false);

    if (resolved.value !== form.getValues('userSilenceTimeout')) {
      setValue('userSilenceTimeout', resolved.value, { shouldDirty: true });
    }

    if (isFluxModel(newModel)) {
      // Both fields are model-locked or re-floored under Flux, so a stale error
      // on them must not keep the form unsubmittable.
      clearErrors(['inboundMessageBuffer', 'userSilenceTimeout']);
    }
  };

  // Per-field test-id: the same pill is rendered next to both locked settings, so
  // an e2e assertion has to be able to tell them apart.
  const lockedToZeroPill = (testId: string) => (
    <Tag
      size="small"
      newVariant="filled"
      variant="default"
      minimal
      test-id={testId}
    >
      Locked to zero for flux
    </Tag>
  );

  // Languages block (PSTUDIO-2950). The catalog drives the locale lists and
  // the checks; validation runs on every render so a choice the channel's
  // speech recognition cannot serve shows up the moment it is made.
  const {
    data: catalog,
    isLoading: catalogLoading,
    error: catalogError,
    retry: retryCatalog,
  } = useVoiceAiLanguages();

  const { transcriptionProvider, speechModel, startingLanguage } =
    watchedValues;
  const watchedLanguages = watchedValues.languages;
  const speechRecognition = useMemo(
    () => ({ transcriptionProvider, speechModel }),
    [transcriptionProvider, speechModel]
  );

  // Without the catalog the locales cannot be checked. An untouched channel
  // stays saveable (Engagement accepted what is stored); once the languages or
  // the speech recognition change, saving them unchecked could store an
  // overlay Engagement rejects at call time, so validation refuses it.
  const catalogUnavailable =
    (catalogLoading || !!catalogError) &&
    !!(
      dirtyFields.languages ||
      dirtyFields.startingLanguage ||
      dirtyFields.transcriptionProvider ||
      dirtyFields.speechModel
    );

  const languagesValidation = useMemo(
    () =>
      validateLanguages({
        languages: watchedLanguages || [],
        startingLanguage,
        speechRecognition,
        catalog,
        catalogUnavailable,
      }),
    [
      watchedLanguages,
      startingLanguage,
      speechRecognition,
      catalog,
      catalogUnavailable,
    ]
  );

  // The validate rule reads the latest values and catalog at submit time, so
  // handleSubmit — the form's Save and the unsaved-changes guard alike —
  // refuses an invalid Languages block without either knowing about it.
  const latestLanguagesValidation = useRef(languagesValidation);
  latestLanguagesValidation.current = languagesValidation;
  register('startingLanguage', {
    validate: () =>
      latestLanguagesValidation.current.isValid ||
      latestLanguagesValidation.current.summary ||
      'Check the languages.',
  });

  const [saveAttempted, setSaveAttempted] = useState(false);

  // One voice picker for the whole form; it serves whichever entry asked.
  const [voiceEntryIndex, setVoiceEntryIndex] = useState(0);
  const voiceEntry = watchedValues.languages?.[voiceEntryIndex];

  const { openModal, voiceSelectorModal } = useVoiceSelectorModal({
    selectedValues: {
      audioModel: voiceEntry?.elevenlabsAudioModel || '',
      language: voiceEntry?.key || '',
      accent: voiceEntry?.accent,
      elevenlabsVoiceId: voiceEntry?.elevenlabsVoiceId || '',
    },
    onVoiceSelect: (voiceId, voiceMetadata) => {
      const languages = getValues('languages') || [];
      setValue(
        'languages',
        languages.map((entry, i) =>
          i === voiceEntryIndex
            ? {
                ...entry,
                elevenlabsVoiceId: voiceId,
                // The modal's type comes from the other codegen target; the
                // shape is the same, as the pre-PSTUDIO-2950 code also cast.
                elevenlabsVoiceMetadata: voiceMetadata as VoiceAiProvider,
              }
            : entry
        ),
        { shouldDirty: true }
      );
    },
  });

  // Callbacks are pluggable so one payload builder serves both the form's own
  // Save (navigate away) and the unsaved-changes guard (resolve, never navigate).
  // PSTUDIO-3897: lets a save parked in `waitForProStudioIdle` notice that the
  // form was discarded before it dispatches. Re-armed on effect setup by the
  // hook — a cleanup-only ref stays `false` after React 18's StrictMode replay
  // and kills every Save in the dev build (see `useIsMountedRef`).
  const isMountedRef = useIsMountedRef();

  const dispatchChannelSave = async (
    data: VoiceChannelFormValues,
    saveCallbacks: { cb: () => void; errorCb?: () => void }
  ) => {
    const { botName, env, ...formData } = data;

    const voiceAiConfig: TwilioConfigInput = buildVoiceAiConfig(formData);

    // A Contact Center voice inbox needs more than the channel config: its own
    // connection and escalation steps that ask for the inbox. Done once the
    // save has landed (see set-up-voice-inbox.ts).
    const inbox =
      formData.laPlatform === CONTACT_CENTER_PLATFORM &&
      formData.laAccountId.trim() &&
      formData.laVoiceInboxId.trim() &&
      projectId
        ? {
            projectId,
            channelId: isUpdating ? channelId : undefined,
            channelName: botName,
            env: env.toLowerCase() as 'live' | 'sandbox',
            accountId: formData.laAccountId.trim(),
            inboxId: formData.laVoiceInboxId.trim(),
          }
        : null;
    const callbacks = !inbox
      ? saveCallbacks
      : {
          ...saveCallbacks,
          cb: () => {
            saveCallbacks.cb();
            setUpVoiceInbox(inbox)
              .then((result) =>
                sendUserMessage({
                  ...voiceInboxSetupMessage(result),
                  autoClose: 8000,
                })
              )
              .catch(() =>
                sendUserMessage({
                  type: 'error',
                  text: 'The channel saved, but connecting its Contact Center voice inbox failed. Save it again to retry.',
                  autoClose: 8000,
                })
              );
          },
        };

    const event = isUpdating
      ? ({
          type: 'updateChannel',
          payload: {
            id: channelId || '',
            type: 'twilio_voice',
            pypeId: '',
            streamId: channelId || '',
            name: botName,
            env: env.toLowerCase() as 'live' | 'sandbox',
            voiceAiConfig,
            ...callbacks,
          },
        } as const)
      : ({
          type: 'createChannel',
          payload: {
            type: 'twilio_voice',
            pypeId: '',
            streamId: '',
            name: botName,
            env: env.toLowerCase() as 'live' | 'sandbox',
            voiceAiConfig,
            ...callbacks,
          },
        } as const);

    // PSTUDIO-3897: `createChannel` / `updateChannel` are handled only in the
    // machine's `idle` state; sent while any flow is running they bubble up and
    // are discarded, so the save produced no mutation and — with no `errorCb` on
    // the plain-Save path — no feedback at all. Wait for the machine to be free.
    try {
      await waitForProStudioIdle(proStudioService);
    } catch (e) {
      sendProStudioEvent({
        type: 'showError',
        error: 'Your changes could not be saved. Please try again.',
      });
      throw e instanceof Error ? e : new Error(String(e));
    }

    // The wait can span seconds, during which Cancel (this form's snackbar has
    // no dismissed-gate, so it stays clickable throughout) or the guard's
    // "Leave" can discard the form. `proStudioService` is a module singleton, so
    // without this the queued dispatch would still fire and persist the
    // discarded edits.
    if (!isMountedRef.current) {
      throw new Error('save-abandoned');
    }

    sendProStudioEvent(event);
  };

  const handleSaveAndLeave = () =>
    new Promise<void>((resolve, reject) => {
      handleSubmit(
        (data) =>
          dispatchChannelSave(data, {
            cb: resolve,
            errorCb: () => reject(new Error('save-failed')),
          }),
        // Nothing is retryable from inside the modal — the invalid fields are
        // behind it — so signal that the guard should close and hand the form
        // back rather than offering "try again".
        () => {
          // The guard hands the form back on an invalid save; show why, or
          // the deferred "choose a voice" errors stay hidden.
          setSaveAttempted(true);
          reject(new UnsavedChangesValidationError());
        }
      )().catch(reject);
    });

  const { modal: unsavedChangesModal, allowNavigationOnce } = useUnsavedChanges(
    {
      isDirty,
      onSave: handleSaveAndLeave,
    }
  );

  const handleFormSubmit: SubmitHandler<VoiceChannelFormValues> = (data) => {
    // PSTUDIO-3897: this path used to pass `cb` only, so a discarded mutation
    // was completely silent. Exactly one of these now always fires.
    const settlement = createSaveSettlement({
      onSuccess: () => {
        setSaveAttempted(false);
        allowNavigationOnce();
        navigate(routes.channels.path);
        form.reset();
      },
      onFailure: () => {
        sendProStudioEvent({
          type: 'showError',
          error: 'Your changes could not be saved. Please try again.',
        });
      },
    });

    return dispatchChannelSave(data, {
      cb: settlement.cb,
      errorCb: settlement.errorCb,
    }).catch(() => {
      // The idle-wait timeout already surfaced its own error and dispatched
      // nothing — drop the settlement so its timer can't toast a second time.
      settlement.cancel();
    });
  };

  const handleCancel = () => {
    setSaveAttempted(false);
    form.reset();
  };

  const handleSave = () => {
    setSaveAttempted(true);
    handleSubmit(handleFormSubmit)();
  };

  const handleCopyWebhookUrl = async () => {
    if (watchedValues.webhookUrl) {
      try {
        await navigator.clipboard.writeText(watchedValues.webhookUrl);
        sendToast({
          type: 'success',
          text: 'Webhook URL copied to clipboard',
          autoClose: 3000,
        });
      } catch (err) {
        sendToast({
          type: 'error',
          text: 'Failed to copy webhook URL',
          autoClose: 5000,
        });
      }
    }
  };
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <PageSection size="small">
        <Stack
          justifyContent="space-between"
          alignItems="center"
          gutter="small"
        >
          <TextTitle size="xsmall" test-id="ps-channel-details-title">
            Channel Details
          </TextTitle>
          <Stack
            alignItems="center"
            gutter="xsmall"
            test-id="ps-voice-icon-label"
          >
            <Icon name="call" size="small" />
            <TextBody>Voice</TextBody>
          </Stack>
        </Stack>
        <Spacer size="large" />

        <Stack>
          <div style={{ flexGrow: 1 }}>
            <TextBody size="small" test-id="ps-channel-name-label">
              Channel Name
            </TextBody>
            <Spacer size="2xsmall" />
            <Input
              variant="outlined"
              test-id={
                isUpdating
                  ? 'ps-update-channel-name-input'
                  : 'ps-create-channel-name-input'
              }
              placeholder="Channel Name"
              value={watchedValues.botName}
              helpText={errors.botName?.message}
              hasError={!!errors.botName}
              {...register('botName', {
                required: 'Channel name is required',
                minLength: {
                  value: 3,
                  message: 'Channel name must be at least 3 characters',
                },
              })}
              onInput={(e) => {
                // @ts-expect-error - InputWC event target has value property
                const { value } = e.target;
                setValue('botName', value, { shouldDirty: true });
                form.trigger('botName');
              }}
            />
          </div>
        </Stack>
        <Spacer size="large" />
        <Stack gutter="large" alignItems="center">
          <TextSubtitle size="small" test-id="ps-channel-environment-label">
            Environment:
          </TextSubtitle>
          <RadioGroup
            value={watchedValues.env}
            hasError={!!errors.env}
            orientation="horizontal"
            test-id={
              isUpdating
                ? 'ps-update-channel-environment-radio-group'
                : 'ps-create-channel-environment-radio-group'
            }
            onChange={(e) => {
              const env = e.target.value as 'sandbox' | 'live';
              setValue('env', env, { shouldDirty: true });
              form.trigger('env');
            }}
          >
            {['sandbox', 'live'].map((env) => (
              <Radio
                checked={watchedValues.env === env}
                key={env}
                value={env}
                disabled={isUpdating}
              >
                {capitalize(env)}
              </Radio>
            ))}
          </RadioGroup>
        </Stack>
        <Spacer size="2xsmall" />
        <Stack gutter="2xsmall" alignItems="center">
          <Icon name="info" size="xsmall" color="gray" />
          <TextCaption variant="tertiary">
            Cannot be changed after channel is created.
          </TextCaption>
        </Stack>
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-speech-model-title">
          Speech Recognition
        </TextTitle>
        <TextBody variant="secondary">
          Choose the speech-to-text provider and model for transcribing voice
          input in real time. They apply to every language of the channel.
        </TextBody>
        <Spacer size="large" />
        <Grid>
          <GridCell xsmall="12" small="6">
            <TextBody size="small">Provider</TextBody>
            <Spacer size="2xsmall" />
            <Select
              variant="outlined"
              value={watchedValues.transcriptionProvider}
              placeholder="Select Provider"
              test-id="ps-voice-channel-stt-provider-select"
              onChange={(e) => {
                const newProvider = e.target.value as 'Google' | 'Deepgram';
                setValue('transcriptionProvider', newProvider, {
                  shouldDirty: true,
                });
                // Reset speechModel to default value for the selected provider
                const newModel =
                  newProvider === 'Google'
                    ? DEFAULT_GOOGLE_SPEECH_MODEL
                    : DEFAULT_DEEPGRAM_SPEECH_MODEL;
                setValue('speechModel', newModel, { shouldDirty: true });
                form.trigger('transcriptionProvider');
                form.trigger('speechModel');
                applyFluxTurnTakingRules(newModel);
              }}
            >
              {watchedValues.transcriptionProvider && (
                <Icon
                  name={
                    watchedValues.transcriptionProvider.toLowerCase() as
                      | 'google'
                      | 'deepgram'
                  }
                  size="small"
                  slot="prefix"
                />
              )}
              <SelectOption value="Google">
                <Icon name="google" size="small" slot="prefix" />
                Google
              </SelectOption>
              <SelectOption value="Deepgram">
                <Icon name="deepgram" size="small" slot="prefix" />
                Deepgram
              </SelectOption>
            </Select>
          </GridCell>
          <GridCell xsmall="12" small="6">
            <TextBody size="small">Speech Model</TextBody>
            <Spacer size="2xsmall" />
            <Select
              variant="outlined"
              value={watchedValues.speechModel}
              placeholder="Select Model"
              test-id="ps-voice-channel-speech-model-select"
              onChange={(e) => {
                const newModel = e.target.value as string;
                setValue('speechModel', newModel, {
                  shouldDirty: true,
                });
                form.trigger('speechModel');
                applyFluxTurnTakingRules(newModel);
              }}
            >
              {(watchedValues.transcriptionProvider === 'Google'
                ? GOOGLE_SPEECH_MODELS
                : DEEPGRAM_SPEECH_MODELS
              ).map((model) => (
                <SelectOption
                  key={model.value}
                  value={model.value}
                  description={model.description}
                >
                  {model.label}
                  {model.recommended && (
                    <Tag
                      slot="suffix"
                      size="small"
                      newVariant="filled"
                      variant="default"
                      minimal
                    >
                      Recommended
                    </Tag>
                  )}
                </SelectOption>
              ))}
            </Select>
            <Spacer size="2xsmall" />
            <Stack gutter="2xsmall" alignItems="center">
              <Icon name="info" size="xsmall" color="gray" />
              <TextCaption variant="tertiary">
                {watchedValues.transcriptionProvider === 'Deepgram' && !isFlux
                  ? 'This model is English only. Use flux or Google for more languages.'
                  : 'flux and Google support multiple languages.'}
              </TextCaption>
            </Stack>
          </GridCell>
        </Grid>
      </PageSection>
      <LanguagesBlock
        languages={watchedValues.languages || []}
        startingLanguage={watchedValues.startingLanguage}
        onLanguagesChange={(languages, nextStartingLanguage) => {
          setValue('languages', languages, { shouldDirty: true });
          setValue('startingLanguage', nextStartingLanguage, {
            shouldDirty: true,
          });
        }}
        catalog={catalog}
        catalogLoading={catalogLoading}
        catalogError={catalogError}
        onRetryCatalog={retryCatalog}
        speechRecognition={speechRecognition}
        validation={languagesValidation}
        showErrors={saveAttempted}
        audioModels={ELEVENLABS_AUDIO_MODELS}
        onChooseVoice={(index) => {
          setVoiceEntryIndex(index);
          openModal();
        }}
      />
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-conversation-handling-title">
          Conversation Handling
        </TextTitle>
        <TextBody variant="secondary">
          Control how the Voice AI paces its responses and handles silence
          during a call. These settings determine how long the agent waits for
          the caller to finish speaking, how it retries when there&apos;s no
          response, and where the call is routed if the caller stays silent.
        </TextBody>
        <Spacer size="large" />

        {isFlux && (
          <>
            <Stack justifyContent="space-between" alignItems="center">
              <Stack alignItems="center" gutter="2xsmall">
                <TextBody size="small">End of Turn Threshold</TextBody>
                <Tooltip
                  placement="right"
                  content="Configures how confident the model must be that the person has stopped speaking before ending the turn."
                >
                  <Icon name="info" color="gray" size="xsmall" />
                </Tooltip>
              </Stack>
              <TextBody size="small">
                {(watchedValues.eotThreshold ?? EOT_THRESHOLD_DEFAULT).toFixed(
                  1
                )}
              </TextBody>
            </Stack>
            <Slider
              step={EOT_THRESHOLD_STEP}
              min={EOT_THRESHOLD_MIN}
              max={EOT_THRESHOLD_MAX}
              value={watchedValues.eotThreshold ?? EOT_THRESHOLD_DEFAULT}
              tooltip="top"
              showMinMaxLabels={false}
              test-id="ps-voice-channel-eot-threshold-slider"
              // @ts-expect-error - TODO: fix this
              onInput={(e) => {
                // A native range input can emit 0.7000000000000001 for a 0.1
                // step, so snap before it reaches form state.
                setValue(
                  'eotThreshold',
                  clampEotThreshold(parseFloat(e.target.value)),
                  {
                    shouldDirty: true,
                  }
                );
              }}
            />
            <Spacer />
          </>
        )}

        <Stack justifyContent="space-between" alignItems="center">
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Inbound Message Buffer</TextBody>
            <Tooltip
              placement="right"
              content="How long the AI waits after the user finishes speaking before processing their message. Set to 0 for an immediate response with no pause."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
            {isFlux &&
              lockedToZeroPill('ps-voice-channel-inbound-buffer-locked-pill')}
          </Stack>
        </Stack>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          // A number input silently drops a non-numeric value, so the locked
          // state has to render as text to show the explanatory copy.
          type={isFlux ? 'text' : 'number'}
          min={0}
          suffix="ms"
          disabled={isFlux}
          value={
            isFlux
              ? FLUX_LOCKED_DISPLAY_VALUE
              : String(watchedValues.inboundMessageBuffer ?? '')
          }
          helpText={errors.inboundMessageBuffer?.message}
          hasError={!!errors.inboundMessageBuffer}
          test-id="ps-voice-channel-inbound-buffer-input"
          {...register('inboundMessageBuffer', {
            setValueAs: (v) =>
              v === '' || v === null || v === undefined ? null : Number(v),
            // Validation is skipped while Flux locks the field — the value it
            // persists comes from resolveConversationHandling, not the input.
            required: isFlux ? false : 'Required',
            min: isFlux
              ? undefined
              : { value: 0, message: 'Must be 0 or greater' },
            validate: (v) =>
              isFlux ||
              (v != null && Number.isInteger(v)) ||
              'Must be a whole number',
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue(
              'inboundMessageBuffer',
              value === '' ? null : parseInt(value, 10),
              {
                shouldDirty: true,
              }
            );
            form.trigger('inboundMessageBuffer');
          }}
        />
        <Spacer />

        <Stack justifyContent="space-between" alignItems="center">
          <Stack alignItems="center" gutter="small">
            {/* Flux always sends a value — a silent caller's retry hangs off it,
                so there is nothing to switch off (PSTUDIO-3941). */}
            {!isFlux && (
              <Toggle
                checked={isMaxSpeakingEnabled}
                test-id="ps-voice-channel-max-speaking-time-toggle"
                onChange={(e) => {
                  setIsMaxSpeakingEnabled(e.target.checked);
                  setValue('userSilenceTimeout', e.target.checked ? 10 : null, {
                    shouldDirty: true,
                  });
                }}
              />
            )}
            <Stack alignItems="center" gutter="2xsmall">
              <TextBody size="small">
                Max Caller Speaking Time Per Turn
              </TextBody>
              <Tooltip
                placement="right"
                content={
                  isFlux
                    ? 'How long Voice AI waits for a silent caller before ending the turn. If the caller stays silent, this is what triggers a retry.'
                    : 'The maximum time a caller can speak in a single turn before their input is processed. This does not affect how long the system waits for the caller to start speaking.'
                }
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
        {/* Kept visible under Flux, so the locked state explains itself rather
            than the field just disappearing. */}
        {(isFlux || isMaxSpeakingEnabled) && (
          <>
            <Spacer size="2xsmall" />
            <Input
              variant="outlined"
              type="number"
              min={isFlux ? FLUX_USER_SILENCE_TIMEOUT_MIN : 2}
              suffix="s"
              value={String(watchedValues.userSilenceTimeout ?? '')}
              helpText={
                errors.userSilenceTimeout?.message ??
                (isFlux ? maxSpeakingHelpText : undefined)
              }
              hasError={!!errors.userSilenceTimeout}
              test-id="ps-voice-channel-max-speaking-time-input"
              {...maxSpeakingTimeField}
              onInput={(e) => {
                // @ts-expect-error - InputWC event target has value property
                const { value } = e.target;
                // Typing below the floor is allowed while the field has focus —
                // it is raised on blur, so a value can still be retyped digit by
                // digit (design frame 1000003108).
                setRaisedToMinimum(false);
                // The user has taken ownership of this value, so leaving Flux
                // must keep it rather than restore what the seed overwrote.
                preFluxSilenceRef.current = null;
                setValue(
                  'userSilenceTimeout',
                  value === '' ? null : parseInt(value, 10),
                  {
                    shouldDirty: true,
                  }
                );
                form.trigger('userSilenceTimeout');
              }}
              onBlur={(e) => {
                // Chained, not replaced: this prop is declared after the
                // register() spread and the DS Input maps onBlur to the `blur`
                // event, so last-wins would drop RHF's own handler and with it
                // touchedFields for this field.
                void maxSpeakingTimeField.onBlur(e);
                if (!isFlux) return;
                const current = form.getValues('userSilenceTimeout');
                const floored = clampFluxUserSilenceTimeout(current);
                if (floored === current) return;
                // Raise rather than reject: the design corrects the value and
                // says so, instead of blocking the save (frame 1000003109).
                setValue('userSilenceTimeout', floored, { shouldDirty: true });
                setRaisedToMinimum(true);
                form.trigger('userSilenceTimeout');
              }}
            />
            {!isFlux && (
              <>
                <Spacer size="2xsmall" />
                <TextCaption variant="tertiary">
                  We recommend leaving this off. Setting a maximum may cut off
                  callers before they finish speaking.
                </TextCaption>
              </>
            )}
          </>
        )}
        <Spacer />

        <Stack justifyContent="space-between" alignItems="center">
          <Stack alignItems="center" gutter="small">
            <Toggle
              checked={
                watchedValues.promptMaxRetries !== null &&
                watchedValues.promptMaxRetries !== undefined
              }
              test-id="ps-voice-channel-max-retries-toggle"
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue('promptMaxRetries', undefined, {
                    shouldDirty: true,
                  });
                  setValue('retryPromptPrefix', undefined, {
                    shouldDirty: true,
                  });
                } else {
                  setValue('promptMaxRetries', 3, { shouldDirty: true });
                  setValue(
                    'retryPromptPrefix',
                    "I'm sorry, I didn't quite catch that.",
                    { shouldDirty: true }
                  );
                }
              }}
            />
            <Stack alignItems="center" gutter="2xsmall">
              <TextBody size="small">Max Retries</TextBody>
              <Tooltip
                placement="right"
                content="Number of times the Voice AI Agent will repeat its last message if no input is detected."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
          </Stack>
          {watchedValues.promptMaxRetries !== null &&
            watchedValues.promptMaxRetries !== undefined && (
              <TextBody size="small">
                {watchedValues.promptMaxRetries ?? 0}
              </TextBody>
            )}
        </Stack>
        {watchedValues.promptMaxRetries !== null &&
          watchedValues.promptMaxRetries !== undefined && (
            <>
              <Slider
                step={1}
                min={0}
                max={5}
                value={watchedValues.promptMaxRetries ?? 0}
                tooltip="top"
                showMinMaxLabels={false}
                test-id="ps-voice-channel-max-retries-slider"
                // @ts-expect-error - TODO: fix this
                onInput={(e) => {
                  setValue('promptMaxRetries', parseInt(e.target.value, 10), {
                    shouldDirty: true,
                  });
                }}
              />
              <Spacer />

              <Stack alignItems="center" gutter="2xsmall">
                <TextBody size="small">Retry Message</TextBody>
                <Tooltip
                  placement="right"
                  content="Voice AI will say the retry message, then the original message if the user doesn't respond."
                >
                  <Icon name="info" color="gray" size="xsmall" />
                </Tooltip>
              </Stack>
              <RetryMessageInput
                value={watchedValues.retryPromptPrefix ?? ''}
                onChange={(val) =>
                  setValue('retryPromptPrefix', val, { shouldDirty: true })
                }
              />
            </>
          )}
        <Spacer />
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-telephony-connection-title">
          Telephony Connection
        </TextTitle>
        <TextBody variant="secondary">
          Please paste the following into your Twillio account to complete the
          Voice AI setup. Reference documentation here.
        </TextBody>
        {/* <Spacer size="large" /> */}
        {/* <TextBody size="small">Phone Number Name</TextBody> */}
        {/* <Spacer size="2xsmall" />
        <Select
          variant="outlined"
          value={watchedValues.phoneNumber}
          onChange={(e) => {
            setValue('phoneNumber', e.target.value as string, {
              shouldDirty: true,
            });
          }}
        >
          <SelectOption value="number1">ADT Twilio Test Number</SelectOption>
          <SelectOption value="number2">ADT Twilio Test Number 2</SelectOption>
          <SelectOption value="number3">ADT Twilio Test Number 3</SelectOption>
        </Select> */}
        <Spacer />
        <TextBody size="small">Twilio Account SID</TextBody>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          value={watchedValues.accountSid}
          helpText={errors.accountSid?.message}
          hasError={!!errors.accountSid}
          test-id="ps-voice-channel-account-sid-input"
          {...register('accountSid', {
            required: 'Twilio Account SID is required',
            // pattern: {
            //   value: /^AC[a-f0-9]{32}$/,
            //   message: 'Invalid Twilio Account SID format',
            // },
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue('accountSid', value, {
              shouldDirty: true,
            });
            form.trigger('accountSid');
          }}
        >
          <Icon name="check" size="small" slot="suffix" />
        </Input>
        <Spacer />
        <TextBody size="small">Twilio Auth Token</TextBody>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          type="password"
          disableAutofill
          value={watchedValues.authToken}
          helpText={errors.authToken?.message}
          hasError={!!errors.authToken}
          test-id="ps-voice-channel-auth-token-input"
          {...register('authToken', {
            required: 'Twilio Auth Token is required',
            // minLength: {
            //   value: 32,
            //   message: 'Auth token must be at least 32 characters',
            // },
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue('authToken', value, {
              shouldDirty: true,
            });
            form.trigger('authToken');
          }}
        />
        <Spacer />
        <TextBody size="small">Twilio Phone Number</TextBody>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          value={watchedValues.phoneNumber}
          helpText={errors.phoneNumber?.message}
          hasError={!!errors.phoneNumber}
          test-id="ps-voice-channel-phone-number-input"
          {...register('phoneNumber', {
            required: 'Twilio Phone Number is required',
            pattern: {
              value: /^\+[1-9]\d{1,14}$/,
              message: 'Invalid phone number format',
            },
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue('phoneNumber', value, {
              shouldDirty: true,
            });
            form.trigger('phoneNumber');
          }}
        >
          <Icon name="check" size="small" slot="suffix" />
        </Input>
        <Spacer />
        <TextBody size="small" test-id="ps-webhook-url-label">
          Webhook URL
        </TextBody>
        <Spacer size="2xsmall" />
        {watchedValues.webhookUrl ? (
          <Tooltip placement="top" content="Click to copy webhook URL">
            <div
              onClick={handleCopyWebhookUrl}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                border: '1px solid #dfe1e5',
                minHeight: '40px',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                gap: '12px',
                transition: 'border 250ms ease-in-out',
                cursor: 'pointer',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#8573e7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#dfe1e5';
              }}
            >
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <TextBody
                  size="small"
                  style={{
                    color: '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {watchedValues.webhookUrl}
                </TextBody>
              </div>
              <Icon name="copy" size="small" color="gray" />
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              border: '1px solid #dfe1e5',
              minHeight: '40px',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              gap: '12px',
            }}
          >
            <TextBody size="small" style={{ color: '#6c757d' }}>
              The Webhook URL will be displayed once you fill out the fields
              above and save changes.
            </TextBody>
          </div>
        )}
        <Spacer />
      </PageSection>
      {/* Call Recording (PSTUDIO-4090). Sits under Telephony Connection because
          it is a Twilio feature: the recordings land in the Twilio account
          configured above, and retention is that account's business, not ours.
          Per channel by design — see `enableCallRecording` in
          VoiceChannelFormValues. */}
      <PageSection size="small">
        <Stack
          justifyContent="space-between"
          alignItems="center"
          gutter="large"
        >
          <Stack direction="column" gutter="2xsmall">
            <TextTitle size="xsmall" test-id="ps-call-recording-title">
              Call Recording
            </TextTitle>
            <TextBody variant="secondary">
              Toggle to record calls. Recordings are stored in Twilio. Please
              configure your retention policies within Twilio.
            </TextBody>
          </Stack>
          <Toggle
            checked={watchedValues.enableCallRecording}
            test-id="ps-voice-channel-call-recording-toggle"
            onChange={(e) => {
              setValue('enableCallRecording', e.target.checked, {
                shouldDirty: true,
              });
            }}
          >
            {/* The DS Toggle wraps its input in a <label> and slots children
                inside it, so this IS the control's accessible name — the
                "Call Recording" title is a sibling in another Stack and names
                nothing. Hidden visually so the card still matches the design. */}
            <span style={VISUALLY_HIDDEN}>Call Recording</span>
          </Toggle>
        </Stack>
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-live-agent-platform-title">
          Live Agent Platforms
        </TextTitle>
        <TextBody variant="secondary">
          Enter the required credentials to connect this Voice AI channel to a
          live agent platform for a voice-to-voice escalation.
        </TextBody>
        <Spacer size="large" />
        <TextBody size="small">Platform</TextBody>
        <Spacer size="2xsmall" />
        <Select
          variant="outlined"
          value={watchedValues.laPlatform}
          placeholder="Select Platform"
          test-id="ps-voice-channel-la-platform-select"
          onChange={(e) => {
            setValue('laPlatform', e.target.value as string, {
              shouldDirty: true,
            });
            if (
              e.target.value === CONTACT_CENTER_PLATFORM &&
              !getValues('laAccountId')
            ) {
              setValue(
                'laAccountId',
                contactCenterAccountOf(
                  pypeConnections,
                  toSolutionEnvironment(getValues('env'))
                ),
                { shouldDirty: true }
              );
            }
          }}
        >
          {watchedValues.laPlatform === 'Genesys' && (
            <Icon name="genesys" size="small" slot="prefix" />
          )}
          <SelectOption value="Genesys">
            <Icon name="genesys" size="small" slot="prefix" />
            Genesys
          </SelectOption>
          <SelectOption value={CONTACT_CENTER_PLATFORM}>
            Contact Center (voice inbox)
          </SelectOption>
        </Select>
        <Spacer />
        {watchedValues.laPlatform === CONTACT_CENTER_PLATFORM ? (
          <>
            <TextBody variant="secondary" size="small">
              Escalated calls ring in this Contact Center voice inbox. Create
              the inbox in Contact Center with App ID{' '}
              <strong>
                {integratorIdOf(watchedValues.webhookUrl) ??
                  'shown here once the channel is saved'}
              </strong>
              . Saving connects the inbox and adds the escalation steps to the
              draft; publish to make them live.
            </TextBody>
            <Spacer />
            <Grid>
              <GridCell xsmall="12" small="6">
                <TextBody size="small">Contact Center account ID</TextBody>
                <Spacer size="2xsmall" />
                <Input
                  variant="outlined"
                  value={watchedValues.laAccountId}
                  autocomplete="off"
                  test-id="ps-voice-channel-la-account-id-input"
                  {...register('laAccountId')}
                  onInput={(e) => {
                    // @ts-expect-error - InputWC event target has value property
                    setValue('laAccountId', e.target.value, {
                      shouldDirty: true,
                    });
                  }}
                />
              </GridCell>
              <GridCell xsmall="12" small="6">
                <TextBody size="small">Voice inbox ID</TextBody>
                <Spacer size="2xsmall" />
                <Input
                  variant="outlined"
                  value={watchedValues.laVoiceInboxId}
                  autocomplete="off"
                  test-id="ps-voice-channel-la-voice-inbox-id-input"
                  {...register('laVoiceInboxId')}
                  onInput={(e) => {
                    // @ts-expect-error - InputWC event target has value property
                    setValue('laVoiceInboxId', e.target.value, {
                      shouldDirty: true,
                    });
                  }}
                />
              </GridCell>
            </Grid>
          </>
        ) : (
          <>
            <TextBody size="small">Grant Type</TextBody>
            <Spacer size="2xsmall" />
            <Input
              variant="outlined"
              value={watchedValues.laGrantType}
              test-id="ps-voice-channel-la-grant-type-input"
              {...register('laGrantType')}
              onInput={(e) => {
                // @ts-expect-error - InputWC event target has value property
                setValue('laGrantType', e.target.value, { shouldDirty: true });
              }}
            />
            <Spacer />
            <Grid>
              <GridCell xsmall="12" small="6">
                <TextBody size="small">Client ID</TextBody>
                <Spacer size="2xsmall" />
                <Input
                  variant="outlined"
                  value={watchedValues.laClientId}
                  test-id="ps-voice-channel-la-client-id-input"
                  {...register('laClientId')}
                  onInput={(e) => {
                    // @ts-expect-error - InputWC event target has value property
                    setValue('laClientId', e.target.value, {
                      shouldDirty: true,
                    });
                  }}
                />
              </GridCell>
              <GridCell xsmall="12" small="6">
                <TextBody size="small">Client Secret</TextBody>
                <Spacer size="2xsmall" />
                <Input
                  variant="outlined"
                  type="password"
                  value={watchedValues.laClientSecret}
                  test-id="ps-voice-channel-la-client-secret-input"
                  {...register('laClientSecret')}
                  onInput={(e) => {
                    // @ts-expect-error - InputWC event target has value property
                    setValue('laClientSecret', e.target.value, {
                      shouldDirty: true,
                    });
                  }}
                />
              </GridCell>
            </Grid>
            <Spacer />
            <TextBody size="small">Company URL</TextBody>
            <Spacer size="2xsmall" />
            <Input
              variant="outlined"
              value={watchedValues.laInstanceUrl}
              test-id="ps-voice-channel-la-instance-url-input"
              {...register('laInstanceUrl')}
              onInput={(e) => {
                // @ts-expect-error - InputWC event target has value property
                setValue('laInstanceUrl', e.target.value, {
                  shouldDirty: true,
                });
              }}
            />
            <Spacer />
            <TextBody size="small">OAuth URL</TextBody>
            <Spacer size="2xsmall" />
            <Input
              variant="outlined"
              value={watchedValues.laOAuthUrl}
              test-id="ps-voice-channel-la-oauth-url-input"
              {...register('laOAuthUrl')}
              onInput={(e) => {
                // @ts-expect-error - InputWC event target has value property
                setValue('laOAuthUrl', e.target.value, { shouldDirty: true });
              }}
            />
          </>
        )}
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-handoff-rules-title">
          Routing Rules
        </TextTitle>
        <TextBody variant="secondary">
          Route escalations by skill. Each skill routes to its assigned
          destination.
        </TextBody>
        <Spacer size="large" />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '0',
            width: '100%',
          }}
        >
          {/* Header row */}
          <div style={{ padding: '8px 12px 8px 0' }}>
            <Stack alignItems="center" gutter="2xsmall">
              <TextCaption
                style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Skill
              </TextCaption>
              <Tooltip
                placement="right"
                content="Skills are defined in Workflows. Add a skill there to make it available here."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
          </div>
          <div style={{ padding: '8px 12px', textAlign: 'center' }}>
            <TextCaption
              style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              Type
            </TextCaption>
          </div>
          <div style={{ padding: '8px 0 8px 12px' }}>
            <Stack alignItems="center" gutter="2xsmall">
              <TextCaption
                style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Destination
              </TextCaption>
              <Tooltip
                placement="right"
                content="The destination (phone number or SIP URI) Voice AI transfers the call to when this skill is triggered."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
          </div>

          {handoffFields.map((field, index) => (
            <React.Fragment key={field.id}>
              {index > 0 && (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    borderTop: '1px solid rgba(100,100,120,0.15)',
                  }}
                />
              )}

              {/* Skill Tag cell */}
              <div
                style={{
                  padding: '8px 12px 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <SkillTagSelect
                  value={watchedValues.handoffRules?.[index]?.skillTag ?? ''}
                  isGeneral={index === 0}
                  usedSkillTags={(watchedValues.handoffRules ?? [])
                    .filter((_, i) => i !== index)
                    .map((r) => r.skillTag)
                    .filter(Boolean)}
                  testId={`ps-voice-channel-handoff-skill-tag-${index}`}
                  onChange={(val) =>
                    setValue(`handoffRules.${index}.skillTag`, val, {
                      shouldDirty: true,
                    })
                  }
                />
              </div>

              {/* Handoff Type cell */}
              <div
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HandoffTypeSelect
                  value={
                    watchedValues.handoffRules?.[index]?.type ?? 'phone_number'
                  }
                  testId={`ps-voice-channel-handoff-type-${index}`}
                  onChange={(val) => {
                    setValue(`handoffRules.${index}.type`, val, {
                      shouldDirty: true,
                    });
                    setValue(`handoffRules.${index}.destination`, '', {
                      shouldDirty: true,
                    });
                    form.trigger(`handoffRules.${index}.destination`);
                  }}
                />
              </div>

              {/* Destination cell */}
              <div
                style={{
                  padding: '8px 0 8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <Input
                    variant="outlined"
                    value={watchedValues.handoffRules?.[index]?.destination}
                    placeholder={
                      watchedValues.handoffRules?.[index]?.type === 'sip'
                        ? 'sip:user@example.com'
                        : '+1XXXXXXXXXX'
                    }
                    test-id={`ps-voice-channel-handoff-destination-${index}`}
                    {...register(`handoffRules.${index}.destination`, {
                      validate: (value) => {
                        if (!value) return true;
                        const handoffType =
                          getValues(`handoffRules.${index}.type`) ??
                          'phone_number';
                        if (handoffType === 'sip') {
                          return (
                            /^sip:.+@.+/.test(value) ||
                            'Invalid SIP URI (e.g. sip:user@example.com)'
                          );
                        }
                        return (
                          /^\+[1-9]\d{1,14}$/.test(value) ||
                          'Invalid phone number format'
                        );
                      },
                    })}
                    helpText={
                      errors.handoffRules?.[index]?.destination?.message
                    }
                    hasError={!!errors.handoffRules?.[index]?.destination}
                    onInput={(e) => {
                      const target = e.target as EventTarget & {
                        value: string;
                      };
                      setValue(
                        `handoffRules.${index}.destination`,
                        target.value,
                        { shouldDirty: true }
                      );
                      form.trigger(`handoffRules.${index}.destination`);
                    }}
                  />
                </div>
                <IconButton
                  name="delete"
                  size="small"
                  style={{ visibility: index === 0 ? 'hidden' : 'visible' }}
                  onClick={() => removeHandoffRule(index)}
                />
              </div>
            </React.Fragment>
          ))}

          {/* Add handoff rule row */}
          <div style={{ gridColumn: '1 / -1', padding: '8px 0' }}>
            <AddHandoffRuleButton
              usedSkillTags={(watchedValues.handoffRules ?? []).map(
                (r) => r.skillTag
              )}
              onAdd={() =>
                appendHandoffRule({
                  skillTag: '',
                  destination: '',
                  type: 'phone_number',
                })
              }
            />
          </div>
        </div>
      </PageSection>
      <PortalDestination name="form-snackbar-destination" />
      <Snackbar id="form-snackbar-destination" open={form.formState.isDirty}>
        There are unsaved changes
        <Button
          variant="secondary"
          size="large"
          onClick={handleCancel}
          slot="action"
          test-id="ps-voice-channel-cancel-button"
        >
          Cancel
        </Button>
        <Button
          size="large"
          onClick={handleSave}
          disabled={!isDirty}
          slot="action"
          test-id="ps-voice-channel-save-button"
        >
          Save Changes
        </Button>
      </Snackbar>

      {voiceSelectorModal}
      {unsavedChangesModal}
    </form>
  );
};
